<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[70] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="close"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4">
        <div class="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <header class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">提交{{ operationLabel }}申请</h3>
              <p class="mt-1 text-sm text-slate-500">申请提交后进入审批，审批通过后才落账。</p>
            </div>
            <button type="button" class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" @click="close">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto p-6">
            <div class="mb-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              <label v-if="operationType === 'INVEST'" class="block">
                <span class="form-label">客户名称 <span class="text-red-500">*</span></span>
                <input :value="form.customerName || '请从客户详情发起注资'" readonly class="form-readonly w-full">
              </label>
              <label v-if="needsContract" class="block">
                <span class="form-label">合同名称 <span class="text-red-500">*</span></span>
                <input :value="contractDisplayName" readonly class="form-readonly w-full">
              </label>
              <label v-if="operationType === 'ROLLOVER'" class="block">
                <span class="form-label">批次ID <span class="text-red-500">*</span></span>
                <input v-model.number="form.batchId" type="number" class="form-input w-full" placeholder="请输入批次ID">
              </label>
              <label v-if="operationType === 'INVEST'" class="block">
                <span class="form-label">合同编号</span>
                <input v-model="form.contractNo" class="form-input w-full" placeholder="可不填，由后端编号规则处理">
              </label>
              <label v-if="operationType === 'INVEST'" class="block">
                <span class="form-label">合同名称 <span class="text-red-500">*</span></span>
                <input v-model="form.contractName" class="form-input w-full" placeholder="请输入合同名称">
              </label>
              <label v-if="operationType === 'INVEST'" class="block">
                <span class="form-label">签署日期</span>
                <input v-model="form.signDate" type="date" class="form-input w-full">
              </label>
              <label v-if="['INVEST', 'INCREASE'].includes(operationType)" class="block">
                <span class="form-label">{{ operationLabel }}金额 <span class="text-red-500">*</span></span>
                <input v-model.number="form.amount" type="number" min="0" step="0.01" class="form-input w-full">
              </label>
              <label v-if="operationType === 'DECREASE'" class="block">
                <span class="form-label">减资金额 <span class="text-red-500">*</span></span>
                <input :value="`¥${formatMoney(decreaseTotalAmount)}`" readonly class="form-readonly w-full">
              </label>
              <label v-if="['INVEST', 'INCREASE', 'ROLLOVER'].includes(operationType)" class="block">
                <span class="form-label">年利率（%） <span class="text-red-500">*</span></span>
                <input v-model.number="form.annualRate" type="number" min="0" step="0.0001" class="form-input w-full" placeholder="例如：3.65 表示 3.65%">
              </label>
              <label v-if="['INVEST', 'INCREASE', 'ROLLOVER'].includes(operationType)" class="block">
                <span class="form-label">起息日 <span class="text-red-500">*</span></span>
                <input v-model="form.valueDate" type="date" class="form-input w-full">
              </label>
              <label v-if="['INVEST', 'INCREASE', 'ROLLOVER'].includes(operationType)" class="block">
                <span class="form-label">到期日 <span class="text-red-500">*</span></span>
                <input v-model="form.maturityDate" type="date" class="form-input w-full">
              </label>
            </div>

            <WealthBatchSelector
              v-if="operationType === 'DECREASE'"
              v-model="decreaseItems"
              :contract-id="form.contractId"
              mode="decrease"
              class="mb-5"
            />

            <WealthBatchSelector
              v-if="operationType === 'ROLLOVER' && form.contractId"
              :contract-id="form.contractId"
              :selected-batch-id="form.batchId"
              mode="rollover"
              class="mb-5"
              @update:selected-batch-id="form.batchId = $event"
              @select-row="selectedBatch = $event"
            />

            <div v-if="operationType === 'WITHDRAW'" class="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              撤资申请不传金额，后端按提交时合同剩余本金生成申请快照，落账时再次按实时批次余额计算实际退出金额。
            </div>

            <div v-if="operationType === 'ROLLOVER' && selectedBatch" class="mb-5 grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm md:grid-cols-4">
              <div><span class="text-slate-500">原批次</span><div class="font-mono text-slate-900">{{ selectedBatch.batchNo }}</div></div>
              <div><span class="text-slate-500">剩余本金</span><div class="font-medium text-slate-900">¥{{ formatMoney(selectedBatch.remainingPrincipalAmount) }}</div></div>
              <div><span class="text-slate-500">原起息日</span><div class="text-slate-900">{{ formatDate(selectedBatch.valueDate) }}</div></div>
              <div><span class="text-slate-500">原到期日</span><div class="text-slate-900">{{ formatDate(selectedBatch.maturityDate) }}</div></div>
            </div>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <section class="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div class="form-label">{{ attachmentLabel }}</div>
                    <p class="mt-0.5 text-xs text-slate-500">
                      选择本地文件，提交申请时自动上传并关联到本次申请。
                      <span v-if="props.operationType === 'INVEST'">合同文件仅支持 PDF、Word 文档或图片。</span>
                    </p>
                  </div>
                  <button type="button" class="btn-secondary" @click="triggerFileInput">选择文件</button>
                </div>
                <input ref="fileInput" type="file" multiple class="hidden" :accept="fileAccept" @change="handleFileSelect">

                <div v-if="pendingAttachmentFiles.length" class="mt-3 space-y-2">
                  <div class="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px]">
                    <label class="block">
                      <span class="form-label">存放素材文件夹 <span class="text-red-500">*</span></span>
                      <select v-model.number="form.assetFolderId" class="form-select-native w-full" :disabled="folderLoading">
                        <option :value="null">{{ folderLoading ? '正在加载文件夹...' : '请选择文件夹' }}</option>
                        <option v-for="folder in assetFolderOptions" :key="folder.id" :value="folder.id">{{ folder.path }}</option>
                      </select>
                    </label>
                    <div class="rounded-md bg-white px-3 py-2 text-xs text-slate-500">
                      已选择 {{ pendingAttachmentFiles.length }} 个文件，提交时上传。
                    </div>
                  </div>
                  <div class="space-y-2">
                    <div v-for="(file, index) in pendingAttachmentFiles" :key="`${file.name}-${file.size}-${index}`" class="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                      <div class="min-w-0">
                        <div class="truncate text-sm font-medium text-slate-900">{{ file.name }}</div>
                        <div class="text-xs text-slate-500">{{ formatFileSize(file.size) }}</div>
                      </div>
                      <button type="button" class="text-sm text-red-600 hover:text-red-700" @click="removeAttachmentFile(index)">移除</button>
                    </div>
                  </div>
                </div>

                <div v-if="uploadedAttachmentAssetIds.length" class="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  已上传 {{ uploadedAttachmentAssetIds.length }} 个文件，提交失败后重试会继续携带这些文件。
                </div>

                <div v-if="!pendingAttachmentFiles.length && !uploadedAttachmentAssetIds.length" class="mt-3 rounded-md border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
                  暂未选择文件
                </div>
              </section>
              <label class="block md:col-span-2">
                <span class="form-label">说明</span>
                <textarea v-model="form.description" rows="3" class="form-textarea w-full" placeholder="请输入申请说明"></textarea>
              </label>
            </div>
          </main>

          <footer class="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button type="button" class="btn-secondary" @click="close">取消</button>
            <button type="button" class="btn-primary" :disabled="isSubmitting" @click="submit">
              {{ uploadingAttachments ? '正在上传文件...' : localSubmitting ? '正在提交...' : '提交申请' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import { createUploadSession, confirmUploadSession, cancelUploadSession, getFolderTree } from '@/api/assets'
import {
  submitWealthDecreaseApply,
  submitWealthIncreaseApply,
  submitWealthInvestApply,
  submitWealthRolloverApply,
  submitWealthWithdrawApply
} from '@/api/wealth'
import WealthBatchSelector from './WealthBatchSelector.vue'
import { cleanPayload, formatDate, formatMoney, getBadgeMeta, toNumber } from '../utils'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  operationType: {
    type: String,
    default: 'INVEST'
  },
  preset: {
    type: Object,
    default: () => ({})
  },
  submitting: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const CONTRACT_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,image/*'
const CONTRACT_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']

const form = reactive(defaultForm())
const decreaseItems = ref([])
const selectedBatch = ref(null)
const fileInput = ref(null)
const pendingAttachmentFiles = ref([])
const uploadedAttachmentAssetIds = ref([])
const folderLoading = ref(false)
const assetFolderOptions = ref([])
const uploadingAttachments = ref(false)
const localSubmitting = ref(false)
const currentUploadSessionId = ref(null)

const operationLabel = computed(() => getBadgeMeta('operation', props.operationType).label)
const needsContract = computed(() => ['INCREASE', 'DECREASE', 'WITHDRAW'].includes(props.operationType))
const attachmentLabel = computed(() => props.operationType === 'INVEST' ? '合同文件' : '申请附件')
const isSubmitting = computed(() => props.submitting || uploadingAttachments.value || localSubmitting.value)
const fileAccept = computed(() => props.operationType === 'INVEST' ? CONTRACT_ATTACHMENT_ACCEPT : undefined)
const contractDisplayName = computed(() => {
  if (!form.contractId) return '请从客户合同发起申请'
  return form.contractName || form.contractNo || `合同 ${form.contractId}`
})
const decreaseTotalAmount = computed(() => decreaseItems.value.reduce((sum, item) => sum + toNumber(item.amount), 0))

function defaultForm() {
  return {
    wealthCustomerId: null,
    customerName: '',
    contractId: null,
    batchId: null,
    contractNo: '',
    contractName: '',
    signDate: '',
    amount: null,
    annualRate: null,
    valueDate: '',
    maturityDate: '',
    description: '',
    assetFolderId: null
  }
}

function reset() {
  Object.assign(form, defaultForm(), props.preset || {})
  decreaseItems.value = []
  selectedBatch.value = null
  pendingAttachmentFiles.value = []
  uploadedAttachmentAssetIds.value = []
  if (fileInput.value) fileInput.value.value = ''
  if (props.operationType === 'INVEST') {
    ensureAssetFoldersLoaded()
  }
}

function close() {
  emit('update:modelValue', false)
}

async function commonPayload() {
  return {
    description: form.description?.trim(),
    attachmentAssetIds: await uploadPendingAttachmentFiles()
  }
}

function validateCommonDates() {
  if (!form.annualRate || !form.valueDate || !form.maturityDate) {
    ElMessage.warning('请填写年利率、起息日和到期日')
    return false
  }
  return true
}

function validateInvestForm() {
  if (!form.wealthCustomerId) {
    ElMessage.warning('请从客户详情发起注资')
    return false
  }
  if (!form.contractName?.trim()) {
    ElMessage.warning('请填写合同名称')
    return false
  }
  if (!form.amount || toNumber(form.amount) <= 0) {
    ElMessage.warning('请填写注资金额')
    return false
  }
  return validateCommonDates()
}

async function submit() {
  if (isSubmitting.value) return
  localSubmitting.value = true
  try {
    if (props.operationType === 'INVEST') {
      if (!validateInvestForm()) return
      await submitWealthInvestApply(cleanPayload({
        wealthCustomerId: form.wealthCustomerId,
        contractNo: form.contractNo?.trim(),
        contractName: form.contractName?.trim(),
        signDate: form.signDate,
        amount: form.amount,
        annualRate: form.annualRate,
        valueDate: form.valueDate,
        maturityDate: form.maturityDate,
        ...(await commonPayload())
      }))
    } else if (props.operationType === 'INCREASE') {
      if (!form.contractId || !form.amount || !validateCommonDates()) return
      await submitWealthIncreaseApply(form.contractId, cleanPayload({
        contractId: form.contractId,
        amount: form.amount,
        annualRate: form.annualRate,
        valueDate: form.valueDate,
        maturityDate: form.maturityDate,
        ...(await commonPayload())
      }))
    } else if (props.operationType === 'DECREASE') {
      if (!form.contractId || decreaseTotalAmount.value <= 0 || !decreaseItems.value.length) {
        ElMessage.warning('请选择合同和减资批次')
        return
      }
      await submitWealthDecreaseApply(form.contractId, cleanPayload({
        contractId: form.contractId,
        amount: decreaseTotalAmount.value,
        batchItems: decreaseItems.value,
        ...(await commonPayload())
      }))
    } else if (props.operationType === 'WITHDRAW') {
      if (!form.contractId) {
        ElMessage.warning('请选择合同')
        return
      }
      await submitWealthWithdrawApply(form.contractId, cleanPayload({
        contractId: form.contractId,
        ...(await commonPayload())
      }))
    } else if (props.operationType === 'ROLLOVER') {
      if (!form.batchId || !validateCommonDates()) return
      await submitWealthRolloverApply(form.batchId, cleanPayload({
        batchId: form.batchId,
        annualRate: form.annualRate,
        valueDate: form.valueDate,
        maturityDate: form.maturityDate,
        ...(await commonPayload())
      }))
    }
    ElMessage.success(`${operationLabel.value}申请已提交`)
    emit('submitted')
    close()
  } finally {
    localSubmitting.value = false
  }
}

function triggerFileInput() {
  fileInput.value?.click()
}

function handleFileSelect(event) {
  const files = Array.from(event.target.files || [])
  if (files.length) {
    const acceptedFiles = files.filter(isAllowedAttachmentFile)
    if (acceptedFiles.length) {
      pendingAttachmentFiles.value = [...pendingAttachmentFiles.value, ...acceptedFiles]
      ensureAssetFoldersLoaded()
    }
  }
  event.target.value = ''
}

function isAllowedAttachmentFile(file) {
  if (props.operationType !== 'INVEST') return true
  return isAllowedContractAttachmentFile(file)
}

function isAllowedContractAttachmentFile(file) {
  const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
  const allowed = file.type?.startsWith('image/') || CONTRACT_ATTACHMENT_EXTENSIONS.includes(extension)
  if (!allowed) {
    ElMessage.warning('合同文件仅支持 PDF、Word 文档或图片')
  }
  return allowed
}

function removeAttachmentFile(index) {
  pendingAttachmentFiles.value.splice(index, 1)
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function getAssetType(file) {
  if (file.type?.startsWith('image/')) return 'IMAGE'
  if (file.type?.startsWith('video/')) return 'VIDEO'
  return 'FILE'
}

function assetNameOf(file) {
  const dotIndex = file.name.lastIndexOf('.')
  return dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name
}

async function ensureAssetFoldersLoaded() {
  if (assetFolderOptions.value.length || folderLoading.value) return
  folderLoading.value = true
  try {
    const tree = await getFolderTree()
    assetFolderOptions.value = flattenManageableFolders(tree || [])
    if (!form.assetFolderId && assetFolderOptions.value.length) {
      form.assetFolderId = assetFolderOptions.value[0].id
    }
  } catch {
    // request interceptor handles visible error
  } finally {
    folderLoading.value = false
  }
}

function flattenManageableFolders(nodes, parentPath = '') {
  return nodes.flatMap(node => {
    const path = parentPath ? `${parentPath} / ${node.name}` : node.name
    const current = node.permissionLevel === 'MANAGE' && !node.pathOnly ? [{ id: node.id, path }] : []
    return [...current, ...flattenManageableFolders(node.children || [], path)]
  })
}

async function uploadPendingAttachmentFiles() {
  if (!pendingAttachmentFiles.value.length) return uploadedAttachmentAssetIds.value
  if (!form.assetFolderId) {
    ElMessage.warning('请选择合同文件存放文件夹')
    throw new Error('asset folder required')
  }
  uploadingAttachments.value = true
  try {
    const assetIds = []
    for (const file of pendingAttachmentFiles.value) {
      assetIds.push(await uploadOneAttachmentFile(file))
    }
    uploadedAttachmentAssetIds.value = [...uploadedAttachmentAssetIds.value, ...assetIds]
    pendingAttachmentFiles.value = []
    if (fileInput.value) fileInput.value.value = ''
    return uploadedAttachmentAssetIds.value
  } finally {
    uploadingAttachments.value = false
  }
}

async function uploadOneAttachmentFile(file) {
  if (!isAllowedAttachmentFile(file)) {
    throw new Error('unsupported attachment file type')
  }
  try {
    const credential = await createUploadSession({
      folderId: form.assetFolderId,
      assetType: getAssetType(file),
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size
    })
    currentUploadSessionId.value = credential.sessionId
    await axios.put(credential.uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...(credential.headers || {})
      }
    })
    const asset = await confirmUploadSession(credential.sessionId, {
      uploadToken: credential.uploadToken,
      name: assetNameOf(file),
      description: props.operationType === 'INVEST' ? '财富合同文件' : '财富申请附件',
      tagIds: []
    })
    currentUploadSessionId.value = null
    return asset.id
  } catch (error) {
    await cancelPendingUploadSession()
    throw error
  }
}

async function cancelPendingUploadSession() {
  if (!currentUploadSessionId.value) return
  const sessionId = currentUploadSessionId.value
  currentUploadSessionId.value = null
  try {
    await cancelUploadSession(sessionId)
  } catch {
    // 上传会话取消失败不覆盖原始提交错误
  }
}

watch(() => props.modelValue, value => {
  if (value) reset()
})

watch(() => props.operationType, reset)
watch(() => props.preset, reset, { deep: true })
</script>
